import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts/index'
import gameIframe from '@ps/bridge/iframe/iframe'
import _ from 'lodash'
import { LAYOUT_RESOLUTION } from '@ps/bridge/qc/scripts/layout'
import { getScript as dragonGetScript } from '@ps/bridge/qc/scripts/dragon-bone'

interface dragonBonAnimationSplitProp {
  animationName: string
  loop: boolean
  speed: number
}

export interface VerticalHorizontalScript {
  adapterData: {
    dragonBoneAnimation?: {
      [key: string]: dragonBonAnimationSplitProp
    }
    [key: string]: any
  } // 横竖屏适配数据
}

/**
 * ps.VerticalHorizontal
 * @param node
 * @param addScriptIfScriptNotExist
 */
export const getScript = (
  node: qc.Node,
  addScriptIfScriptNotExist = true,
): VerticalHorizontalScript | undefined => {
  const config: VerticalHorizontalScript | undefined = node.getScript(
    scripts.verticalHorizontal.name,
  )
  if (config) {
    return config
  }
  if (addScriptIfScriptNotExist) {
    return node.addScript(
      scripts.verticalHorizontal.name,
    ) as VerticalHorizontalScript
  }
  return config
}

/**
 * 检测当前项目有没有VerticalHorizontal.js,用于兼容旧项目
 */
export const hasVerticalHorizontalScript = (): boolean => {
  return !!gameIframe.ps.VerticalHorizontal
}

/**
 * 设置横竖屏适配数据
 * @param node 处理的目标节点
 * @param key 需要适配的属性
 * @param value 需要适配的属性的值
 */
export const setAdpaterData = (
  node: qc.Node,
  key: string,
  value: string,
  adapter: string,
): void => {
  if (!hasVerticalHorizontalScript()) return
  const vhScript = getScript(node) as VerticalHorizontalScript
  _.set(vhScript.adapterData, `${key}.${adapter}`, value)
}

/**
 * 删除动画时同步删除其横竖屏数据
 * @param node 处理的目标节点
 * @param uuid TweenPosition的uuid
 */
export const removeTweenPositionData = (node: qc.Node, uuid: string): void => {
  if (!hasVerticalHorizontalScript()) return
  const vhScript = getScript(node, false)
  if (vhScript && vhScript.adapterData.tweenPosition[uuid]) {
    delete vhScript.adapterData.tweenPosition[uuid]
  }
}

export const getTweenPositionData = (
  node: qc.Node,
  uuid: string,
  adapter: LAYOUT_RESOLUTION,
): VerticalHorizontalScript['adapterData'] | null => {
  if (!hasVerticalHorizontalScript()) return null
  const vhScript = getScript(node) as VerticalHorizontalScript
  if (
    vhScript &&
    vhScript.adapterData.tweenPosition[uuid] &&
    vhScript.adapterData.tweenPosition[uuid].from &&
    vhScript.adapterData.tweenPosition[uuid].to &&
    vhScript.adapterData.tweenPosition[uuid].from[adapter] &&
    vhScript.adapterData.tweenPosition[uuid].to[adapter]
  ) {
    return {
      from: vhScript.adapterData.tweenPosition[uuid].from[adapter],
      to: vhScript.adapterData.tweenPosition[uuid].to[adapter],
    }
  }
  return null
}

/**
 * 通过 key 去更新龙骨横竖屏属性值
 * @param node 节点
 * @param key 节点的 key
 * @param adapter 分辨率
 * @param value 值
 */
export const setSkPlayerDataByKey = (
  node: qc.Node,
  key: string,
  adapter: LAYOUT_RESOLUTION,
  value: string | boolean | number,
): void => {
  if (!hasVerticalHorizontalScript()) return
  const vhScript = getScript(node) as VerticalHorizontalScript
  _.set(vhScript.adapterData, `dragonBoneAnimation.${adapter}.${key}`, value)
}

/**
 * 获取不同分辨率下的龙骨属性值
 * @param node 节点
 * @param adapter 分辨率
 */
export const getSkPlayerData = (
  node: qc.Node,
  adapter: LAYOUT_RESOLUTION,
): dragonBonAnimationSplitProp | undefined => {
  if (!hasVerticalHorizontalScript()) return
  const vhScript = getScript(node) as VerticalHorizontalScript
  return _.get(vhScript.adapterData, `dragonBoneAnimation.${adapter}`)
}

/**
 * 设置不同分辨率下的龙骨属性值
 * @param node 节点
 * @param adapter 分辨率
 */
export const setSkPlayerData = (
  node: qc.Node,
  adapter: LAYOUT_RESOLUTION,
  value: dragonBonAnimationSplitProp,
): void => {
  if (!hasVerticalHorizontalScript()) return
  const vhScript = getScript(node) as VerticalHorizontalScript
  _.set(vhScript.adapterData, `dragonBoneAnimation.${adapter}`, value)
}

/**
 * 默认添加不同分辨率下的数值
 * @param node 节点
 * @param adapter 分辨率
 */
export const resetSkPlayerData = (node: qc.Node): void => {
  if (!hasVerticalHorizontalScript()) return
  const dragonBoneScriptData = dragonGetScript(node)
  if (dragonBoneScriptData) {
    const vhData: dragonBonAnimationSplitProp = {
      // @ts-ignore
      animationName:
        dragonBoneScriptData.animationName ||
        (node.animationNameList && node.animationNameList[0]),
      loop: dragonBoneScriptData.loop,
      speed: dragonBoneScriptData.speed,
    }
    setSkPlayerData(node, 'ldef', _.cloneDeep(vhData))
    setSkPlayerData(node, 'pdef', _.cloneDeep(vhData))
  }
}
